import { useRef, useState } from "react";
import { UploadCloud, Loader2, Sparkles, Copy, Check, AlertTriangle } from "lucide-react";
import { streamForm, currentLimits } from "../lib/api";

const MODELS = [
    { id: "claude", label: "Claude Sonnet 5.5", provider: "Anthropic" },
    { id: "gpt", label: "GPT 5.6 Terra", provider: "OpenAI" },
    { id: "gemini", label: "Gemini 3.1 Pro", provider: "Google" },
];

export function ModelPicker({ model, setModel, disabled }) {
    return (
        <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Modelo de IA" data-testid="model-picker">
            {MODELS.map((m) => (
                <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={model === m.id}
                    disabled={disabled}
                    onClick={() => setModel(m.id)}
                    data-testid={`model-${m.id}`}
                    className={`border-2 border-black p-3 text-left transition-colors disabled:opacity-50 ${
                        model === m.id ? "bg-black text-[#22D3EE]" : "bg-white hover:bg-[#F0F9FF]"
                    }`}
                >
                    <span className="block font-bold text-sm">{m.label}</span>
                    <span className={`block font-mono-tag text-[10px] uppercase ${model === m.id ? "text-gray-400" : "text-gray-500"}`}>
                        {m.provider}
                    </span>
                </button>
            ))}
        </div>
    );
}

export function AiDropzone({ file, setFile, busy, label = "ARRASTE SEU PDF" }) {
    const inputRef = useRef(null);
    const LIMITS = currentLimits();
    return (
        <div
            role="button"
            tabIndex={0}
            aria-label="Enviar PDF"
            data-testid="ai-dropzone"
            onClick={() => !busy && inputRef.current?.click()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
                e.preventDefault();
                if (busy) return;
                const f = e.dataTransfer.files?.[0];
                if (f) setFile(f);
            }}
            className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
                file ? "border-[#0284C7] bg-[#F0F9FF]" : "border-gray-400 hover:border-black"
            } ${busy ? "opacity-60 cursor-wait" : ""}`}
        >
            <UploadCloud className="w-8 h-8 mx-auto text-black mb-2" aria-hidden="true" />
            <span className="font-poster text-xl text-black block">
                {file ? file.name.toUpperCase() : label}
            </span>
            <span className="font-mono-tag text-xs text-gray-500">PDF · até {LIMITS.maxFileMB} MB</span>
            <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                data-testid="ai-file-input"
                onChange={(e) => {
                    if (e.target.files?.[0]) setFile(e.target.files[0]);
                    e.target.value = "";
                }}
            />
        </div>
    );
}

/** Ferramenta: Resumir PDF com IA */
export function ResumirPdf() {
    const [file, setFile] = useState(null);
    const [model, setModel] = useState("claude");
    const [phase, setPhase] = useState("idle"); // idle | streaming | done | failed
    const [summary, setSummary] = useState("");
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const process = async () => {
        if (!file) return;
        setPhase("streaming");
        setSummary("");
        setError(null);
        setMeta(null);
        const form = new FormData();
        form.append("file", file);
        form.append("model", model);
        try {
            await streamForm("/ai/resumir", form, {
                onMeta: setMeta,
                onDelta: (d) => setSummary((s) => s + d),
                onError: (msg) => { setError(msg); setPhase("failed"); },
                onDone: () => setPhase((p) => (p === "failed" ? p : "done")),
            });
            setPhase((p) => (p === "streaming" ? "done" : p));
        } catch (err) {
            setError(err.message);
            setPhase("failed");
        }
    };

    const copy = async () => {
        await navigator.clipboard.writeText(summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const busy = phase === "streaming";

    return (
        <div className="border-2 border-black bg-white hard-shadow p-4 space-y-4" data-testid="ai-resumir">
            <ModelPicker model={model} setModel={setModel} disabled={busy} />
            <AiDropzone file={file} setFile={setFile} busy={busy} />
            <button
                onClick={process}
                disabled={!file || busy}
                data-testid="ai-process-button"
                className="w-full py-4 bg-[#0284C7] hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-poster text-2xl tracking-wide transition-colors flex items-center justify-center gap-3"
            >
                {busy ? <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" /> : <Sparkles className="w-6 h-6" aria-hidden="true" />}
                {busy ? "RESUMINDO…" : "RESUMIR AGORA"}
            </button>

            {error && (
                <div className="border-2 border-black bg-[#FEF2F2] p-4" role="alert" data-testid="ai-error-box">
                    <p className="font-bold text-sm text-red-900 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" aria-hidden="true" /> Não deu certo
                    </p>
                    <p className="text-xs text-red-800 mt-1">{error}</p>
                </div>
            )}

            {(summary || busy) && (
                <div className="border-2 border-black bg-[#FAFAF9] p-5" data-testid="ai-summary-box">
                    <div className="flex items-center justify-between mb-3">
                        <span className="font-mono-tag text-[11px] font-bold uppercase text-[#0284C7]">
                            {meta ? `Resumo · ${meta.model} · ${meta.pages} pág.` : "Resumo"}
                        </span>
                        {phase === "done" && summary && (
                            <button
                                onClick={copy}
                                data-testid="copy-summary-button"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-black bg-white font-mono-tag text-[11px] font-bold uppercase hover:bg-black hover:text-white transition-colors"
                            >
                                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                {copied ? "Copiado" : "Copiar"}
                            </button>
                        )}
                    </div>
                    <div className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap" data-testid="ai-summary-text">
                        {summary}
                        {busy && <span className="inline-block w-2 h-4 bg-[#0284C7] animate-pulse ml-0.5 align-middle" aria-hidden="true" />}
                    </div>
                    {phase === "done" && (
                        <p className="mt-3 font-mono-tag text-[10px] text-gray-500">
                            Conteúdo processado por IA — revise antes de usar. O documento e a sessão expiram em até 60 min.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
